import { useState, useEffect, useMemo, useCallback } from 'react';

const useInfiniteScroll = ({
    nextToken,
    loadMore,
}: {
    nextToken?: string | boolean;
    loadMore?: () => Promise<void>;
}) => {
    const [infiniteElement, setInfiniteElement] = useState<Element | null>(null);
    const [loadMoreData, setLoadMoreData] = useState<boolean>(false);

    // infinite scroll
    const useOnScreen = (node: Element | null) => {
        const [isIntersecting, setIntersecting] = useState(false);

        const option = {
            root: null,
            rootMargin: '20px',
            threshold: 0,
        };

        const observer = useMemo(
            () => new IntersectionObserver(([entry]) => setIntersecting(!!entry?.isIntersecting), option),
            [node]
        );

        useEffect(() => {
            if (node) {
                observer.observe(node);
            }

            return () => {
                observer.disconnect();
            };
        }, [node]);

        return isIntersecting;
    };

    const loaderRef = useCallback((node: Element | null) => {
        if (node !== null) {
            setInfiniteElement(node);
        }
    }, []);
    const isIntersecting = useOnScreen(infiniteElement);

    useEffect(() => {
        if (isIntersecting) {
            setLoadMoreData(true);
        }
    }, [isIntersecting]);

    useEffect(() => {
        if (loadMoreData && nextToken) {
            loadMore?.()
                .then(() => {
                    setLoadMoreData(false);
                })
                .finally(() => {
                    setLoadMoreData(false);
                });
        } else {
            setLoadMoreData(false);
        }
    }, [loadMoreData]);

    return {
        loadMoreData,
        loaderRef,
    };
};

export default useInfiniteScroll;
